/**
 * yojo-quick-add-selector.js — Wave 4 Batch B (variant quick-add system).
 *
 * Replaces the multi-variant "+" PDP-navigation stub in
 * snippets/yojo-product-card.liquid with a real selector: bottom sheet on
 * mobile, anchored popover on desktop, built from ONE shared portal
 * (snippets/yojo-quick-add-portal.liquid, rendered once in layout/theme.liquid)
 * instead of one modal per card.
 *
 * Event delegation only (document-level listeners) — no per-card binding —
 * so triggers injected later by collection-grid load-more / section
 * re-renders work without re-initialization (the IntersectionObserver-rebind
 * lesson: docs/preview-reconciliation memory "yojo-shelf-rollout-flag-trap"
 * class of bug, and E6/G4's own card-host fragmentation note).
 *
 * State machine (instructions/02):
 *   idle -> loading-product -> selecting -> adding -> added -> closed
 * with explicit error branches (product-data failure, invalid selection,
 * add failure, variant-goes-unavailable, duplicate click).
 *
 * Cart-add pipeline: POSTs to the SAME endpoint/contract
 * component-product-form.js uses (`${routes.cart_add_url}.js`, body
 * `id&quantity&sections=helper-cart&sections_url=<path>`) and updates the
 * same DOM targets (#AjaxCartForm, [data-header-cart-count/-total],
 * #site-cart-sidebar, #screen-reader-info). This is intentionally NOT a
 * requestSubmit() against the existing hidden <product-form> — that path
 * dispatches 'add-to-cart' on both success AND 422 failure (the theme's
 * cart drawer surfaces errors inline), which gives no clean success/failure
 * signal for THIS component's required truthful adding/added/error states.
 * Hitting the identical endpoint with the identical params/response
 * handling is the same pipeline, not a parallel one — just a call site that
 * can tell the two apart.
 */
(function () {
  'use strict';

  if (window.YojoQuickAddSelector) return; // idempotent (section reloads)

  var BREAKPOINT = 768;
  /* Home/category pass 2026-10-01 (H3): how long the sheet shows "נוסף ✓"
     before it closes. The cart drawer opens only AFTER the sheet's close
     transition, so the confirmation never sits on top of the drawer (it used
     to cover the total, the delivery gap line and checkout for ~2.5 s). */
  var ADDED_MS = 500;

  /* 2026-10-01 (swarm a15-F7): the sheet showed Shopify's raw English 422 text
     ("You can't add more ... to the cart."). Every message the shopper can see
     here is Hebrew. Wording: copy desk rows Q2, Q3, Q4
     (audit/website-swarm-2026-10-01/decisions/copy-strings.md §8). */
  var MSG_FAILED = 'לא הצלחנו להוסיף לסל. אפשר לנסות שוב.';
  var MSG_NETWORK = 'לא הצלחנו להוסיף לסל. כדאי לבדוק את החיבור ולנסות שוב.';

  /* Fix round f1 (2026-10-02): a refused add (422) used to say "not enough
     stock, choose a smaller quantity" to a shopper who asked for one unit.
     For one unit there are two real causes, and the cart tells them apart:
     the cart already holds every unit in stock (Q2, cart.stock_limit_all_in_cart),
     or the size sold out since the page loaded (Q3, the dictionary's
     products.product.variant_sold_out_or_unavailable). An add of more than one
     unit keeps the shared count/generic lines (Q1), matching "Only N" rather
     than the first number, which could be the "0.5" in a title. The same
     guarded definition sits in assets/component-product-form.js; whichever
     file loads first wins. Resolves to the line to show; never rejects. */
  window.yojoAddRefusedMessage = window.yojoAddRefusedMessage || function (variantId, qtyAsked, json) {
    var s = (window.KROWN && KROWN.settings) || {};
    var L = s.locales || {};
    var raw = String((json && (json.description || json.message)) || '');
    var only = raw.match(/Only (\d+)/i);
    if (qtyAsked > 1) {
      return Promise.resolve(only && L.cart_stock_limit_count
        ? L.cart_stock_limit_count.replace('{{ count }}', only[1])
        : (L.cart_stock_limit_generic || 'הכמות שבחרתם לא זמינה במלאי. אפשר לבחור כמות קטנה יותר.'));
    }
    var soldOut = L.products_variant_sold_out_or_unavailable || 'האפשרות הזו אזלה מהמלאי או לא זמינה';
    return fetch(((s.routes && s.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = ((cart && cart.items) || []).some(function (item) { return String(item.variant_id) === String(variantId); });
        return inCart ? (L.cart_stock_limit_all_in_cart || 'כל הכמות שיש במלאי כבר בסל.') : soldOut;
      })
      .catch(function () { return soldOut; });
  };

  /* K5 (swarm 2026-10-02; a11y ruling R8, "one message per add"). The card "+",
     the PDP companions, the drawer's gap closers and the size sheet wrote the
     generic "המוצר נוסף לסל" the moment an add landed, and
     yojo-a11y-cart-announcer.js (the one writer for cart changes) then said
     "נוסף לסל: <product>." 300 ms later, so every add was spoken twice.
     Copied from watchCartLine() in assets/yojo-pdp-v2.js (the PDP and the
     mixed box already work this way): the watch starts when the add is sent,
     the announcer speaks alone, and the add path says its own specific line
     only if no cart line reached the live region 3 s after the add landed
     (the announcer's /cart.js read failed or was throttled), so an add is
     still always announced. Units are spelled out for the screen reader (R5):
     "נוסף לסל: פטל אדום קפוא, 2 קילו." Same guarded definitions in
     assets/component-product-form.js; whichever file loads first wins. */
  window.yojoWatchCartLine = window.yojoWatchCartLine || function () {
    var CART_LINE = /^(נוסף לסל|נוספה לסל|עודכנה כמות|הסל עודכן)/;
    var sr = document.getElementById('screen-reader-info');
    var heard = false, mo = null;
    if (sr && window.MutationObserver) {
      mo = new MutationObserver(function () { if (CART_LINE.test((sr.textContent || '').trim())) heard = true; });
      mo.observe(sr, { childList: true, characterData: true, subtree: true });
    }
    function stop() { if (mo) { mo.disconnect(); mo = null; } }
    return {
      stop: stop,
      // After a successful add: speak `text` only if no cart line has been heard 3 s from now.
      settle: function (text) {
        if (!sr || !text) { stop(); return; }
        setTimeout(function () {
          stop();
          if (heard) return;
          sr.textContent = '';
          setTimeout(function () {
            sr.textContent = text;
            setTimeout(function () { if (sr.textContent === text) sr.textContent = ''; }, 4000);
          }, 30);
        }, 3000);
      }
    };
  };
  // The fallback line from the /cart/add.js answer (one line item, or { items: [...] }).
  window.yojoAddedLine = window.yojoAddedLine || function (item) {
    item = (item && item.items && item.items[0]) || item || {};
    var spoken = function (s) {
      return String(s || '')
        .replace(/ק["״]ג/g, 'קילו')
        .replace(/מ["״]ל/g, 'מיליליטר')
        .replace(/ס["״]מ/g, 'סנטימטר')
        .replace(/(\d)\s*ג['׳]/g, '$1 גרם')
        .replace(/\s+/g, ' ')
        .trim();
    };
    var name = spoken(item.product_title);
    var size = item.variant_title && !/^Default Title$/i.test(item.variant_title) ? spoken(item.variant_title) : '';
    if (!name) {
      var L = (window.KROWN && KROWN.settings && KROWN.settings.locales) || {};
      return L.cart_announcement || 'המוצר נוסף לסל';
    }
    return 'נוסף לסל: ' + name + (size ? ', ' + size : '') + '.';
  };

  var productCache = new Map(); // handle -> Promise<productJson>
  var portal = null;
  var els = {};
  var current = null; // { handle, trigger, product, groups, selections, variant }
  var lastFocused = null;

  /* 2026-10-01 (swarm a10-F4): Back (Android key, iOS swipe-back) with the
     sheet open left the page: on a category page it went to the homepage and
     the shopper lost their place. Opening the sheet now adds one same-URL
     history entry, Back closes the sheet, and closing it any other way
     (close button, Esc, backdrop, after an add) steps back over that entry, so
     history is exactly as it was. The entry carries the page's current
     searchParams so the facet filter's popstate handler (component-facets.js)
     sees no change if a shopper steps Forward into it, and it carries no
     yhcsCat, so the homepage shelf (yojo-cat-shelf.js) ignores it. Internal
     re-opens (retry, crossing the breakpoint) keep the entry.

     Fix round f1 (2026-10-02):
     - Phone sheet only. The desktop/tablet popover is not scroll-locked, so
       the shopper can scroll while it is open, and the history.back() that
       removes the entry restored the scroll position saved when it opened:
       closing it jumped the page back 420px (Chromium 1440). The popover no
       longer adds an entry; the locked sheet cannot move, so its step back
       leaves the page where it is.
     - Each entry carries a token. Forward after a close lands on the spent
       entry; it is stepped past at once, so Forward does nothing visible
       instead of leaving a dead Back step. An entry left from another page
       load (a reload with the sheet open) has another token and is ignored.
     - Leaving for the product page from the sheet's load-error state replaces
       the entry (see the click handler), so Back from that page is one step. */
  var HISTORY_MARK = 'quick-add';
  var historyToken = null; // our entry's token while it is the one on top
  var spentToken = null; // the last entry we stepped back over

  function pushHistory() {
    if (historyToken || !window.history || typeof history.pushState !== 'function') return;
    if (window.Shopify && Shopify.designMode) return;
    var token = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    try {
      history.pushState({ yojoOverlay: HISTORY_MARK, yojoOverlayToken: token, searchParams: window.location.search.slice(1) }, '');
      historyToken = token;
    } catch (e) {
      /* sandboxed frame: the sheet works without the Back step */
    }
  }

  function popHistory() {
    if (!historyToken) return;
    var token = historyToken;
    historyToken = null;
    spentToken = token;
    if (history.state && history.state.yojoOverlayToken === token) {
      try { history.back(); } catch (e) { /* ignore */ }
    }
  }

  window.addEventListener('popstate', function (e) {
    var token = e.state && e.state.yojoOverlay === HISTORY_MARK ? e.state.yojoOverlayToken : null;
    if (!historyToken) {
      if (token && token === spentToken) {
        try { history.back(); } catch (err) { /* ignore */ }
      }
      return;
    }
    if (token === historyToken) return;
    spentToken = historyToken;
    historyToken = null; // the browser already left our entry
    if (current) close();
  });

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function ensurePortal() {
    if (portal) return portal;
    portal = document.getElementById('yojo-quick-add-portal');
    if (!portal) return null;
    els = {
      backdrop: qs(portal, '[data-yqa-backdrop]'),
      panel: qs(portal, '[data-yqa-panel]'),
      thumb: qs(portal, '[data-yqa-thumb]'),
      title: qs(portal, '[data-yqa-title]'),
      price: qs(portal, '[data-yqa-price]'),
      body: qs(portal, '[data-yqa-body]'),
      loading: qs(portal, '[data-yqa-loading]'),
      status: qs(portal, '[data-yqa-status]'),
      add: qs(portal, '[data-yqa-add]'),
      addLabel: qs(portal, '[data-yqa-add-label]'),
      close: qs(portal, '[data-yqa-close]'),
    };
    return portal;
  }

  function isMobile() {
    return window.matchMedia('(max-width: ' + (BREAKPOINT - 0.02) + 'px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function motionDuration() {
    if (prefersReducedMotion()) return 0;
    return isMobile() ? 300 : 200;
  }

  /** Collision guard: consent/newsletter modal-box currently open (instructions/02
   *  "quick add cannot open behind consent"). */
  /* R4 Wave 3 (D-13) — an overlay blocks quick-add only if it GENUINELY blocks.
     This used to return true for any `modal-box.active`, which on a first visit
     meant the cookie-consent banner silently swallowed every quick-add: open()
     returned early, nothing opened, nothing was said, and the customer simply
     could not add to cart until they dismissed cookies.

     Measured on the preview at 390x844 — the consent element is not a modal:
       rect y=743 h=101   covers 12% of the viewport, pinned to the bottom
       aria-modal         null          role="dialog", position:fixed, z 1001
       backdrop           none
       elementFromPoint over the quick-add button -> BUTTON.yhp-qa itself
     The button was fully hit-testable the whole time; only this guard stopped it.

     Consent is NOT bypassed. A real modal — anything marking itself
     aria-modal="true" — still takes priority, and a non-modal overlay that
     actually covers the trigger still wins, which is the case the original rule
     was reaching for. What no longer happens is a bottom banner disabling
     commerce across the entire page. */
  function overlapsTrigger(el, trigger) {
    if (!trigger || !trigger.getBoundingClientRect) return false;
    var a = el.getBoundingClientRect();
    var b = trigger.getBoundingClientRect();
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
  }

  function blockingOverlayActive(trigger) {
    var els = document.querySelectorAll('modal-box.active, modal-box[aria-hidden="false"]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.getClientRects().length) continue;               // not rendered at all
      var cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      if (el.getAttribute('aria-modal') === 'true') return true; // a genuine modal
      if (overlapsTrigger(el, trigger)) return true;             // covers this button
    }
    return false;
  }

  /* Swarm 2026-10-02 (b3-polish): the sheet prints prices and sizes the way
     the card does since the 26 Sep card ruling, "21 ₪ · 0.5 ק״ג": whole
     shekels (a trailing .00 goes, 17.90 stays, like Liquid's
     money_without_trailing_zeros) and the unit with gershayim. Display only:
     the radio values, the variant lookup and what is added to the cart keep
     the catalogue spelling. */
  function money(cents) {
    var s = '';
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      try {
        s = Shopify.formatMoney(cents, window.KROWN && KROWN.settings && KROWN.settings.moneyFormat);
      } catch (e) {
        s = '';
      }
    }
    if (!s) s = (cents / 100).toFixed(2) + ' ₪';
    return String(s).replace(/([0-9])[.,]00(?![0-9])/, '$1');
  }

  function unitDisplay(str) {
    return String(str == null ? '' : str).replace(/"/g, '״');
  }

  /* D-3 (journey upgrade 2026-10-21, sweep C-1b): the sheet's size cells carry
     the PDP buy box's two value cues — the computed "הכי משתלם" tag and the
     ₪/100 ג׳ line — so the quick-add default (0.5 ק"ג) is no longer chosen
     blind. Same rules as snippets/yojo-pdp-form-standard.liquid: weight read
     from the variant title (ק"ג/ק״ג), tag only on the strictly lowest ₪/ק"ג
     among AVAILABLE variants, no tag on ties or when no per-kg derives. */
  function kgOf(title) {
    var t = String(title == null ? '' : title);
    if (t.indexOf('ק"ג') === -1 && t.indexOf('ק״ג') === -1) return 0;
    var n = parseFloat(t.replace(/ק["״]ג/g, '').trim());
    return isFinite(n) && n > 0 ? n : 0;
  }

  function per100gLabel(variant) {
    var kg = variant ? kgOf(variant.title) : 0;
    if (!kg) return '';
    return money(Math.round(variant.price / (kg * 10))) + ' / 100 ג׳';
  }

  function bestValueVariantId(product) {
    var best = 0;
    var bestId = 0;
    var tie = false;
    (product.variants || []).forEach(function (v) {
      if (!v.available) return;
      var kg = kgOf(v.title);
      if (!kg) return;
      var perKg = v.price / kg;
      if (!best || perKg < best) {
        best = perKg;
        bestId = v.id;
        tie = false;
      } else if (perKg === best) {
        tie = true;
      }
    });
    return tie ? 0 : bestId;
  }

  function fetchProduct(handle, url) {
    if (productCache.has(handle)) return productCache.get(handle);
    // A7-01 (2026-09-03): search-result cards carry the product URL WITH the
    // search engine's own tracking query string (?_pos=…&_sid=…&_ss=r), so
    // `url + '.js'` requested e.g. /products/x?_pos=3&_sid=…&_ss=r.js — never a
    // real route, always a 404 — and quick-add silently failed on every search
    // result. Strip the query string and hash before appending '.js'.
    var cleanUrl = url.split('?')[0].split('#')[0];
    var p = fetch(cleanUrl + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('product-fetch-failed:' + r.status);
      return r.json();
    });
    productCache.set(handle, p);
    p.catch(function () {
      productCache.delete(handle); // allow retry
    });
    return p;
  }

  function buildGroups(product) {
    var options = product.options || [];
    return options.map(function (option, idx) {
      // Shopify's product.js `options` entries are plain strings on some
      // API/theme versions and `{name, position, values}` objects on
      // others (this store returns objects) — normalize so the legend
      // never renders String({...}) => "[object Object]".
      var name = option && typeof option === 'object' ? option.name : option;
      var seen = {};
      var values = [];
      product.variants.forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val != null && !seen[val]) {
          seen[val] = true;
          values.push(val);
        }
      });
      return { name: name, index: idx, values: values };
    });
  }

  function variantMatches(v, selections, ignoreIndex) {
    for (var i = 0; i < selections.length; i++) {
      if (i === ignoreIndex) continue;
      if (selections[i] && v['option' + (i + 1)] !== selections[i]) return false;
    }
    return true;
  }

  function valueAvailable(product, groupIndex, value, selections) {
    return product.variants.some(function (v) {
      if (v['option' + (groupIndex + 1)] !== value) return false;
      if (!variantMatches(v, selections, groupIndex)) return false;
      return v.available;
    });
  }

  function findVariant(product, selections) {
    if (selections.some(function (s) { return !s; })) return null;
    return (
      product.variants.filter(function (v) {
        return selections.every(function (s, i) {
          return v['option' + (i + 1)] === s;
        });
      })[0] || null
    );
  }

  function defaultSelections(product, groups, preferredVariantId) {
    /* Audit F12 (2026-08-14): the card prices itself on the canonical default
       variant (snippets/yojo-default-variant.liquid — "one rule answers which
       variant this product opens on") and carries that variant's id in its
       hidden form input. This selector ignored it and re-derived
       first-available, so a card advertising "38.00 ₪ · 1 ק"ג" opened a
       popover preselected at "21.00 ₪ · 0.5 ק"ג" — a price switch at the
       moment of intent. Honor the card's variant when it exists and is
       available; first-available stays the fallback. */
    var base = null;
    if (preferredVariantId) {
      base = product.variants.find(function (v) {
        return String(v.id) === String(preferredVariantId) && v.available;
      }) || null;
    }
    base = base ||
      product.variants.find(function (v) {
        return v.available;
      }) || product.variants[0];
    return groups.map(function (g) {
      return base ? base['option' + (g.index + 1)] : g.values[0];
    });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* Attribute context needs MORE than escapeHtml: textContent→innerHTML
     escapes < > & but NOT quotes, and Hebrew weight titles contain a
     literal double quote (ק"ג — gershayim typed as "). Injected into
     value="…" the attribute parsed as `0.5 ק`, the truncated value round-
     tripped through input.value, matched no variant, and every radio
     un-checked itself — the "choose a variant, can't add" bug (owner
     report 2026-08-12, reproduced on frozen-fruit-mango-cut @390). */
  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function render() {
    if (!current || !current.product) return;
    var product = current.product;
    var groups = current.groups;
    var selections = current.selections;
    var variant = findVariant(product, selections);
    current.variant = variant;

    els.title.textContent = product.title;
    var img = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image;
    if (img) {
      els.thumb.style.backgroundImage = 'url(' + img.replace(/\.(jpg|jpeg|png|webp)/i, '_200x$&') + ')';
    }

    if (variant) {
      var priceHtml = money(variant.price);
      if (variant.compare_at_price > variant.price) {
        priceHtml += ' <del>' + money(variant.compare_at_price) + '</del>';
      }
      var unit = variant.title !== 'Default Title' ? variant.title : '';
      if (unit) priceHtml += ' <i>· ' + escapeHtml(unitDisplay(unit)) + '</i>';
      els.price.innerHTML = priceHtml;
    } else {
      els.price.textContent = '';
    }

    var html = '';
    var bestId = bestValueVariantId(product); // D-3: PDP's computed best-value rule
    groups.forEach(function (group) {
      html += '<fieldset class="yqa-option-group"><legend class="yqa-option-group__legend">' + escapeHtml(group.name) + '</legend><div class="yqa-option-group__values">';
      group.values.forEach(function (value, vi) {
        var available = valueAvailable(product, group.index, value, selections);
        var checked = selections[group.index] === value;
        var inputId = 'yqa-opt-' + group.index + '-' + vi;
        // A3-03 (2026-09-03): chips showed only the option label ("0.5 ק"ג"),
        // never a price, so choosing a pack size in the popover meant guessing
        // the cost until after selecting it — the PDP pack tiles already show
        // price per pill; the popover didn't. Find a representative variant for
        // this value (matching the OTHER current selections when possible, so
        // the shown price tracks what picking it would actually select) and
        // render its price as a second line. Sold-out chips get the same
        // "אזל" mark the PDP tiles use, not just reduced opacity.
        var repVariant =
          product.variants.filter(function (v) {
            return v['option' + (group.index + 1)] === value && variantMatches(v, selections, group.index);
          })[0] ||
          product.variants.filter(function (v) {
            return v['option' + (group.index + 1)] === value;
          })[0];
        // Swarm 2026-10-02 (f3-polish, c09 R5 item 3): the pill's spoken name ran size and price together
        // ("0.5 ק״ג15 ₪"); a visually hidden comma separates them for screen readers, the look is unchanged.
        var srSep = '<span class="visually-hidden">, </span>';
        var priceLine = repVariant ? srSep + '<span class="yqa-option-value__price">' + money(repVariant.price) + '</span>' : '';
        var oosLine = available ? '' : srSep + '<span class="yqa-option-value__oos">אזל</span>';
        // D-3: the PDP's two value cues on the same cells (see helpers above).
        // The tag is decorative like the PDP's (aria-hidden); the per-100g line
        // is part of the spoken name, after its own comma.
        var per100 = available && repVariant ? per100gLabel(repVariant) : '';
        var per100Line = per100 ? srSep + '<span class="yqa-option-value__per100">' + escapeHtml(per100) + '</span>' : '';
        var bestTag = available && repVariant && bestId && repVariant.id === bestId
          ? '<span class="yqa-option-value__tag" aria-hidden="true">הכי משתלם</span>'
          : '';
        html +=
          '<span class="yqa-option-value"' + (available ? '' : ' data-unavailable') + '>' +
          '<input type="radio" id="' + inputId + '" name="yqa-group-' + group.index + '" value="' + escapeAttr(value) + '" data-yqa-option data-group-index="' + group.index + '"' +
          (checked ? ' checked' : '') + (available ? '' : ' aria-disabled="true"') + '>' +
          '<label for="' + inputId + '">' + bestTag + '<span class="yqa-option-value__text">' + escapeHtml(unitDisplay(value)) + '</span>' + priceLine + per100Line + oosLine + (available ? '' : ' <span class="visually-hidden">(אזל מהמלאי)</span>') + '</label>' +
          '</span>';
      });
      html += '</div></fieldset>';
    });

    if (variant && !variant.available) {
      html += '<p class="yqa-panel__availability" data-state="sold-out">אזל מהמלאי בשילוב זה</p>';
    }

    els.body.innerHTML = html;

    var canAdd = !!variant && variant.available;
    els.add.disabled = !canAdd || current.state === 'adding';
    if (els.addLabel) {
      els.addLabel.textContent =
        current.state === 'added'
          ? 'נוסף ✓'
          : !variant
          ? 'יש לבחור אפשרות'
          : !variant.available
          ? 'אזל מהמלאי'
          : 'הוספה לסל';
    }
    els.panel.setAttribute('data-yqa-state', current.state || 'selecting');
  }

  function setStatus(msg, isError) {
    els.status.textContent = msg || '';
    els.status.setAttribute('data-state', isError ? 'error' : '');
  }

  /* Height of the cookie banner while it is up (assets/yojo-consent-inset.js
     publishes it on <html>); 0 once it is answered. */
  function consentInset() {
    return parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--yojo-consent-h')) || 0;
  }

  function positionPopover(trigger) {
    var rect = trigger.getBoundingClientRect();
    var panelRect = els.panel.getBoundingClientRect();
    var margin = 16;
    var vw = window.innerWidth;
    // 2026-10-01 (swarm a15-F1): the banner's top is the floor, not the
    // viewport's bottom, so the popover's add button never lands under it.
    var vh = window.innerHeight - consentInset();

    var top = rect.bottom + 8;
    if (top + panelRect.height + margin > vh) {
      var above = rect.top - panelRect.height - 8;
      top = above >= margin ? above : Math.max(margin, vh - panelRect.height - margin);
    }

    var left = rect.left; // viewport coords are direction-agnostic; RTL is
    // handled by the panel's own dir inheritance, not by mirroring math here.
    if (left + panelRect.width + margin > vw) {
      left = vw - panelRect.width - margin;
    }
    if (left < margin) left = margin;

    els.panel.style.top = top + 'px';
    els.panel.style.left = left + 'px';
  }

  /* K5 (swarm 2026-10-02; c09 R2, binding for this lane). The phone sheet is a
     modal layer (aria-modal), but nothing behind it was inert: main, header and
     footer all stayed reachable while it was open, so screen-reader containment
     rested on WebKit's aria-modal support alone. On open the sheet now sets
     `inert` on the page behind, the same list SidebarDrawer.show() uses
     (window.inertElems, i.e. every [data-js-inert]; the footer-group wrapper
     joins it with the cart-a11y fix), and takes it off again before focus goes
     back to the card "+", because focus cannot land on an inert element.
     Elements that were already inert (a drawer open behind the sheet) are left
     as they were. Never an element that holds #screen-reader-info (cart
     announcements keep speaking) or the sheet itself. The Shopify consent
     banner is not in the list, and the desktop popover (not a locked layer)
     sets nothing. */
  var madeInert = [];

  function inertPage() {
    releasePage();
    var sr = document.getElementById('screen-reader-info');
    var list = [].slice.call(window.inertElems || []).concat([].slice.call(document.querySelectorAll('[data-js-inert]')));
    list.forEach(function (el) {
      if (!el || !el.setAttribute || madeInert.indexOf(el) > -1 || el.hasAttribute('inert')) return;
      if (portal && (el.contains(portal) || portal.contains(el))) return;
      if (sr && el.contains(sr)) return;
      el.setAttribute('inert', '');
      madeInert.push(el);
    });
  }

  function releasePage() {
    while (madeInert.length) madeInert.pop().removeAttribute('inert');
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = els.panel.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (!current) return;
    if (e.key === 'Escape') {
      close();
    } else {
      trapFocus(e);
    }
  }

  function open(trigger) {
    if (!ensurePortal()) return;
    if (current) close(true, true);
    if (blockingOverlayActive(trigger)) {
      popHistory();
      // Collision rule: consent has priority; do not open behind it.
      var consent = document.querySelector('modal-box.active, modal-box[aria-hidden="false"]');
      if (consent && consent.focus) consent.focus();
      return;
    }

    lastFocused = trigger;
    var handle = trigger.getAttribute('data-product-handle');
    var url = trigger.getAttribute('data-product-url');
    current = { handle: handle, trigger: trigger, product: null, groups: [], selections: [], variant: null, state: 'loading-product' };

    var mobile = isMobile();
    portal.classList.remove('yqa--sheet', 'yqa--popover');
    portal.classList.add(mobile ? 'yqa--sheet' : 'yqa--popover');
    portal.hidden = false;
    portal.setAttribute('aria-hidden', 'false');
    els.loading.hidden = false;
    els.body.innerHTML = '';
    els.body.appendChild(els.loading);
    setStatus('');
    els.add.disabled = true;
    /* W3 (QA wave 1): the portal is SHARED between opens. Without a full
       visual reset, a previous product's 'added' state leaked into the next
       open — live evidence showed an error panel ("שגיאה בטעינת המוצר") with
       a green "נוסף ✓" button at the same time. Reset every stateful surface
       before the fetch resolves. */
    if (els.addLabel) els.addLabel.textContent = 'הוספה לסל';
    els.panel.setAttribute('data-yqa-state', 'loading-product');
    els.title.textContent = trigger.getAttribute('data-product-title') || '';
    els.price.textContent = '';
    if (els.thumb) els.thumb.style.backgroundImage = '';
    trigger.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', onKeydown, true);

    // Force layout so the browser commits the closed starting state (opacity/
    // transform) before we flip to the open state below — this is what makes
    // the CSS transition actually animate. A requestAnimationFrame hop here
    // is unnecessary (the forced reflow already flushes style) and was
    // leaving a window where the panel was DOM-visible but still in its
    // fully-offscreen/closed transform, which a caller reading geometry
    // immediately after open() could observe.
    // eslint-disable-next-line no-unused-expressions
    els.panel.offsetHeight;
    portal.classList.add('yqa-is-open');
    // Back-to-close for the phone sheet only (see HISTORY_MARK). A sheet that
    // re-opens as a popover after crossing the breakpoint drops its entry.
    if (mobile) pushHistory();
    else popHistory();
    // Phone sheet only: the page behind goes inert before focus moves in (K5, c09 R2).
    if (mobile) inertPage();
    if (!mobile) positionPopover(trigger);

    // Move focus into the dialog immediately (WAI-ARIA dialog pattern) rather
    // than waiting on the network fetch below — a slow product fetch should
    // not leave focus stranded on the trigger.
    els.panel.focus();

    fetchProduct(handle, url)
      .then(function (product) {
        if (!current || current.handle !== handle) return; // superseded
        current.product = product;
        current.groups = buildGroups(product);
        var hiddenInput = findHiddenForm(trigger);
        current.selections = defaultSelections(product, current.groups, hiddenInput && hiddenInput.value);
        current.state = 'selecting';
        render();
        if (!mobile) positionPopover(trigger);
        var firstControl = els.panel.querySelector('input, button:not([data-yqa-close])');
        if (firstControl) firstControl.focus();
      })
      .catch(function () {
        if (!current || current.handle !== handle) return;
        current.state = 'error';
        /* W3: a load failure must present a coherent recovery path — a real
           retry control (fetchProduct dropped the failed cache entry, so a
           retry re-fetches) and the product page as an escape hatch — and
           must never leave the add button actionable. */
        els.body.innerHTML =
          '<p class="yqa-panel__availability" data-state="sold-out">שגיאה בטעינת המוצר.</p>' +
          '<div class="yqa-error-actions" style="display:flex;gap:10px;margin-top:12px;">' +
          '<button type="button" class="button button--outline button--regular" data-yqa-retry style="min-height:44px;flex:1;">לנסות שוב</button>' +
          '<a class="button button--outline button--regular" data-yqa-product-link href="' + escapeAttr(url) + '" style="min-height:44px;flex:1;text-align:center;">לעמוד המוצר</a>' +
          '</div>';
        els.add.disabled = true;
        if (els.addLabel) els.addLabel.textContent = 'הוספה לסל';
        els.panel.setAttribute('data-yqa-state', 'error');
        setStatus('לא ניתן היה לטעון את המוצר.', true);
        var retryBtn = els.body.querySelector('[data-yqa-retry]');
        if (retryBtn) retryBtn.focus();
      });
  }

  function close(skipFocusReturn, keepHistory) {
    if (!current || !portal) return;
    var trigger = current.trigger;
    if (!keepHistory) popHistory();
    document.removeEventListener('keydown', onKeydown, true);
    portal.classList.remove('yqa-is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    // Clear `current` synchronously (not deferred to the close-animation
    // timeout below) so an in-flight fetchProduct().then()/.catch() from
    // this same open cycle sees its `current` guard fail immediately and
    // bails out instead of calling firstControl.focus() / render() after
    // we've already returned focus to the trigger — otherwise a fast
    // Escape-before-fetch-resolves closes the panel, we focus the trigger,
    // then the stale fetch resolves and steals focus back into the panel,
    // which then goes `hidden` and drops focus to <body>.
    current = null;
    var duration = motionDuration();
    var finish = function () {
      portal.hidden = true;
      portal.setAttribute('aria-hidden', 'true');
      els.panel.style.top = '';
      els.panel.style.left = '';
    };
    if (duration === 0) finish();
    else window.setTimeout(finish, duration + 20);
    // The page behind leaves `inert` BEFORE focus goes back: an inert "+" cannot take it (K5).
    releasePage();
    if (!skipFocusReturn && trigger && trigger.focus) trigger.focus();
  }

  function onOptionChange(input) {
    if (!current || !current.product) return;
    var idx = parseInt(input.getAttribute('data-group-index'), 10);
    if (input.hasAttribute('aria-disabled')) return; // guard: unavailable value
    current.selections[idx] = input.value;
    setStatus('');
    render();
  }

  function findHiddenForm(trigger) {
    var card = trigger.closest('.yhp-card');
    return card ? card.querySelector('.yhp-qaform[data-quick-add-variant-input], .yhp-qaform input[data-quick-add-variant-input]') : null;
  }

  function addToCart() {
    /* Audit F4 (2026-08-14): 'adding' alone left a hole — after a successful
       add the panel sits in state 'added' for ADDED_MS before auto-closing,
       and a second click in that window passed this guard and silently added
       another unit (measured on live: 3 rapid clicks → quantity 2). A
       double-click is normal shopper behavior; both in-flight AND completed
       states must swallow it. */
    if (!current || current.state === 'adding' || current.state === 'added') return; // duplicate-click guard
    var variant = current.variant;
    if (!variant || !variant.available) {
      setStatus('נא לבחור אפשרות זמינה במלאי.', true);
      var firstUnselected = els.panel.querySelector('[data-yqa-option]:not(:checked)');
      if (firstUnselected) firstUnselected.focus();
      return;
    }

    current.state = 'adding';
    render();
    setStatus('');
    var cycle = current; // the open sheet this add belongs to

    var body =
      'id=' + encodeURIComponent(variant.id) +
      '&quantity=1&sections=helper-cart&sections_url=' + encodeURIComponent(window.location.pathname);

    // Closure sprint 2026-08-08: order-level upsell attribution. When the
    // trigger that opened this portal came from an upsell surface it carries
    // data-upsell-src; forward it as a HIDDEN line-item property (leading
    // underscore = never rendered in cart/checkout).
    var upsellSrc = current.trigger && current.trigger.getAttribute && current.trigger.getAttribute('data-upsell-src');
    if (upsellSrc) {
      body += '&properties[_yojo_src]=' + encodeURIComponent(upsellSrc);
    }

    // 2026-09-26 (R10): per-variant line properties the card rendered for this
    // variant (today: the frozen-bag packing field, snippets/yojo-pack-instruction
    // .liquid). The PDP form stamps them; this add path used to drop them, so a
    // quick-added 2 kg bag reached the packer as "4 × 0.5 ק"ג". The card owns
    // the name and the value; this only forwards what it finds for the variant.
    var card = current.trigger && current.trigger.closest && current.trigger.closest('.yhp-card');
    if (card) {
      card.querySelectorAll('[data-line-property-for="' + String(variant.id) + '"]').forEach(function (field) {
        if (field.name && field.value) {
          body += '&' + encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value);
        }
      });
    }

    var cartAddUrl = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_add_url) || '/cart/add';

    window._yojoCartMutationsInFlight = (window._yojoCartMutationsInFlight || 0) + 1;

    // K5: one message per add. The watch starts as the add is sent (see yojoWatchCartLine).
    var addWatch = window.yojoWatchCartLine();
    var addSucceeded = false;

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then(function (r) {
        return r.json().then(function (json) {
          return { ok: r.ok && json.status !== 422, status: r.status, json: json || {} };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          addWatch.stop(); // nothing was added: the refusal line below (in the sheet's status) is the one message
          if (current !== cycle) return; // nothing was added and the sheet that asked is gone
          var refused = result.status === 422 || result.json.status === 422;
          // The button stays in its "adding" state while the cart is read.
          return (refused ? window.yojoAddRefusedMessage(variant.id, 1, result.json) : Promise.resolve(MSG_FAILED))
            .then(function (msg) {
              if (current !== cycle) return;
              current.state = 'selecting';
              setStatus(msg, true);
              render();
              els.add.focus();
            });
        }

        /* 2026-08-09: the add is announced from the moment it succeeds, before
           any section re-render, so a failed follow-up fetch (throttle,
           challenge, 5xx) can never leave the screen reader told nothing; the
           site's accessibility statement promises the announcement.
           K5 (2026-10-02, R8): that used to be the generic "המוצר נוסף לסל",
           with the announcer's "נוסף לסל: <product>." 300 ms behind it. The
           announcer now speaks alone; this line is said only if it stays
           silent (yojoWatchCartLine). */
        addSucceeded = true;
        addWatch.settle(window.yojoAddedLine({
          product_title: result.json.product_title || (cycle.product && cycle.product.title),
          variant_title: result.json.product_title ? result.json.variant_title : variant.title,
        }));

        var sectionHtml = result.json.sections && result.json.sections['helper-cart'];
        var applySections = function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cartFormInner = doc.getElementById('AjaxCartForm');
          if (cartFormInner && document.getElementById('AjaxCartForm')) {
            var target = document.getElementById('AjaxCartForm');
            target.innerHTML = cartFormInner.innerHTML;
            if (target.ajaxifyCartItems) target.ajaxifyCartItems();
          }
          document.querySelectorAll('[data-header-cart-count]').forEach(function (elm) {
            var src = doc.querySelector('[data-cart-count]');
            if (src) elm.textContent = src.textContent;
          });
          document.querySelectorAll('[data-header-cart-total]').forEach(function (elm) {
            var src = doc.querySelector('[data-cart-total]');
            if (src) elm.textContent = src.textContent;
          });
          /* A3 (QA wave 1): the footer — subtotal, discounts, free-shipping /
             minimum progress — lives in #AjaxCartSubtotal, OUTSIDE #AjaxCartForm.
             Regular add-to-cart (component-product-form.js) and drawer qty edits
             (component-cart.js) both refresh it via yojoReplaceCartSubtotal;
             quick-add skipped it, so the drawer opened over a stale footer
             (0.00 ₪ on a fresh visit). Same canonical pipeline, same event. */
          if (window.yojoReplaceCartSubtotal) window.yojoReplaceCartSubtotal(doc);
          document.dispatchEvent(new CustomEvent('cart:updated'));
        };

        var proceed = function (html) {
          if (html) {
            try { applySections(html); } catch (e) { /* the add itself succeeded */ }
          }
          /* 2026-10-01 (swarm a15-F2): closing the sheet while the add was in
             flight used to drop everything after the add (`if (!current)
             return;` sat above): the item was in the cart, but the header
             badge and the drawer kept the old count and no cart:updated
             fired. The repaint above now always runs; only the sheet's own
             "added" state needs the sheet. The drawer opens as it would have,
             unless another sheet has been opened in the meantime. */
          if (current !== cycle) {
            window.setTimeout(function () {
              if (current) return;
              var drawer = document.getElementById('site-cart-sidebar');
              if (drawer && drawer.show) drawer.show();
            }, motionDuration() + 20); // after the sheet's own close transition
            return;
          }
          /* the add's announcement is settled at add-success time (see above);
             nothing is said here, or AT hears it twice. */
          current.state = 'added';
          render();

          // Feedback parity with the single-variant path: flash the visible
          // "+" pill via the existing capture-phase listener
          // (assets/yojo-quick-add.js) by dispatching the same event name on
          // the trigger button it already looks for inside `.yhp-card`.
          if (current.trigger) {
            current.trigger.dispatchEvent(new Event('add-to-cart'));
          }

          /* H3: sheet first, drawer second. Showing the drawer here (as this
             used to) put it UNDER the still-open sheet (z 1001 vs 1010).
             If the shopper closed the sheet themselves in the meantime,
             `current` is no longer this cycle and the drawer still opens. */
          window.setTimeout(function () {
            var wait = 0;
            if (current === cycle) {
              close();
              wait = motionDuration() + 20;
            }
            window.setTimeout(function () {
              if (current) return; // another sheet is open now; the drawer would sit under it
              var sidebar = document.getElementById('site-cart-sidebar');
              if (sidebar && sidebar.show) sidebar.show();
            }, wait);
          }, ADDED_MS);
        };

        if (sectionHtml) {
          proceed(sectionHtml);
        } else {
          fetch('?section_id=helper-cart')
            .then(function (r2) { return r2.text(); })
            .then(proceed)
            .catch(function () { proceed(null); });
        }
      })
      .catch(function () {
        if (!addSucceeded) addWatch.stop();
        if (current !== cycle) return;
        current.state = 'selecting';
        setStatus(MSG_NETWORK, true);
        render();
        els.add.focus();
      })
      .finally(function () {
        window._yojoCartMutationsInFlight = Math.max(0, (window._yojoCartMutationsInFlight || 1) - 1);
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-trigger]');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    if (!current) return;
    var productLink = e.target.closest('[data-yqa-product-link]');
    if (productLink && historyToken && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
      /* Leaving from the load-error state: the product page takes the sheet's
         own history entry (replace, not push), so Back from it returns to
         this page in one step instead of first landing on the spent entry. */
      e.preventDefault();
      historyToken = null;
      window.location.replace(productLink.href);
      return;
    }
    if (e.target.closest('[data-yqa-retry]')) {
      /* W3: retry re-opens the same trigger; the failed product fetch was
         evicted from the cache, so this is a genuine re-request. */
      var retryTrigger = current.trigger;
      if (retryTrigger) open(retryTrigger);
      return;
    }
    if (e.target.closest('[data-yqa-close]')) {
      close();
      return;
    }
    if (e.target.closest('[data-yqa-backdrop]')) {
      close();
      return;
    }
    if (e.target.closest('[data-yqa-add]')) {
      addToCart();
    }
  });

  document.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('[data-yqa-option]')) {
      onOptionChange(e.target);
    }
  });

  window.addEventListener(
    'resize',
    function () {
      if (!current || !current.product) return;
      var mobile = isMobile();
      var wantsSheet = portal.classList.contains('yqa--sheet');
      if (mobile !== wantsSheet) {
        // Viewport crossed the breakpoint mid-open: re-open cleanly in the
        // other mode rather than leaving a mismatched layout.
        var trigger = current.trigger;
        close(true, true);
        open(trigger);
      } else if (!mobile) {
        positionPopover(current.trigger);
      }
    },
    { passive: true }
  );

  window.YojoQuickAddSelector = { open: open, close: close };
})();
